import { dailyRate, getEnabledPeriodPrices, splitPeriodRows } from "@1-now/booking-core";
import { hasNoPrice, isNotBookable } from "utils/carPrice";

/**
 * The room a fleet card keeps for its rate tiers (1NOW-11110).
 *
 * The price block sits at the bottom of the card and its height was whatever the car's own tiers
 * made it, so a car with 3 tiers put its "FROM $X /day" higher than a car with 1 (1455, 1477,
 * 1499 and 1521px down one row). Every card now holds the room for the most tier rows any car
 * shows, plus the "+N more rates" line when any car needs it, so the main price and the button
 * sit at the same height in every card.
 *
 * Design A keeps the "Request price" button of a car with no price in that same area, under the
 * "Price on request" line, so that line sits at the height of every other card's main price (a
 * priced card has no button, and the button used to make the block taller and push the priced
 * cards' price down by its height, 44px). `request` says some car needs that room.
 */
export const TIER_ROW_PX = 18;
export const TIER_MORE_PX = 22;
/** The Request price button's height in Design A's card (h-9). */
export const REQUEST_BTN_PX = 36;

export interface TierSlots {
  /** The most tier rows a card shows, never more than the package's cap. */
  rows: number;
  /** Some car has tiers beyond the cap, so its "+N more rates" line needs a place. */
  more: boolean;
  /** Some car has no price, so Design A's "Price on request" card and its Request price button need a place. */
  request?: boolean;
}

/** The rows and "more" line the given cars show on a card, for a company's price settings. */
export const tierSlots = (cars: any[] | null | undefined, company: any): TierSlots => {
  const slots: TierSlots = { rows: 0, more: false, request: false };
  (cars ?? []).forEach((car) => {
    if (car && hasNoPrice(car)) slots.request = true;
    if (!car || isNotBookable(car)) return;
    const prices = getEnabledPeriodPrices(dailyRate(car) ?? 0, company, car.period_prices);
    const { shown, hiddenCount } = splitPeriodRows(prices.slice(1), false);
    slots.rows = Math.max(slots.rows, shown.length);
    if (hiddenCount > 0) slots.more = true;
  });
  return slots;
};

/** The height, in px, a tier area holds so that cards with fewer tiers still line up. */
export const tierAreaHeight = ({ rows, more }: TierSlots) => rows * TIER_ROW_PX + (more ? TIER_MORE_PX : 0);

/**
 * The height Design A's area under the main price holds: the tiers' room, and at least the room
 * of the Request price button when any car is on request (a card has one or the other under its
 * price line, never both, so they share the area).
 */
export const priceAreaHeight = (slots: TierSlots) =>
  Math.max(tierAreaHeight(slots), slots.request ? REQUEST_BTN_PX : 0);
